// src/pages/Team/FloorView.jsx
// STATUS: active | UPDATED: 2026-09-10
// LOG:
//   2026-09-10  New. The team surface, and deliberately not a scoreboard.
// What the floor is doing, for the people who work on it.
//
// THIS IS THE ONE SCREEN MOST LIKELY TO BECOME A LEADERBOARD BY ACCIDENT.
//
// Tyler's instruction is that nobody feels left out, and a ranking creates a
// bottom permanently. The person at the bottom is usually the one who most
// needs coaching and is now least likely to ask for it, and on a floor where
// pay is already a ranking a second one adds nothing and costs trust.
//
// So every number here is about a STEP or about the floor as a whole. The one
// place an individual is named is "who to ask", which names them as the person
// who is GOOD at something. Strength is per step, so almost everybody is
// strongest at something, and that turns being good at one thing into a reason
// for two people who sit twenty feet apart to talk.

import { useEffect, useState } from 'react'
import { Box, Button, HStack, Text, VStack, useToast } from '@chakra-ui/react'
import { Check } from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { STEPS } from '../../content/steps'
import { confirmHandoff, handoffsAwaitingMe, HANDOFF_KINDS } from '../../lib/handoffs'
import { firstNameFor } from '../../lib/names.js'

const stepTitle = (n) => STEPS.find((s) => s.number === n)?.title || `Step ${n}`

export default function FloorView({ profile, user, members }) {
  const [awaiting, setAwaiting] = useState([])
  const [busy, setBusy] = useState(null)
  const toast = useToast()
  const dealershipId = profile?.dealership_id

  useEffect(() => {
    if (!dealershipId) return
    handoffsAwaitingMe(user?.id, dealershipId).then(({ data }) => setAwaiting(data))
  }, [dealershipId, user?.id])

  const confirm = async (id) => {
    setBusy(id)
    const { error } = await confirmHandoff(id, user?.id)
    setBusy(null)
    if (error) {
      toast({ status: 'error', title: 'That did not save' })
      return
    }
    setAwaiting((prev) => prev.filter((h) => h.id !== id))
    toast({ status: 'success', title: 'Confirmed' })
  }

  // Per step coverage across the floor. A gap here is a gap in the process,
  // which is a thing a manager can run a meeting about. A gap in a person is
  // not, and is none of this screen's business.
  const [coverage, setCoverage] = useState(null)
  useEffect(() => {
    if (!members || members.length === 0) return
    const ids = members.map((m) => m.id)
    // step_progress HAS NO step_number COLUMN. It has step_id, and the number
    // lives on steps. This asked for step_number for weeks, which Supabase
    // answered with an error, which `data || []` then turned into an empty
    // array, which rendered as a floor where nobody had passed anything. No
    // crash and no message: the section just quietly said nothing.
    //
    // It survived because the floor views have never run with a real floor on
    // them. Found on 2026-09-26 by checking the column names of a query I was
    // about to copy, which is the only reason anybody looked.
    supabase
      .from('step_progress')
      .select('user_id, status, steps(number)')
      .in('user_id', ids)
      .then(({ data, error }) => {
        // An error here used to be indistinguishable from an empty floor. Now
        // coverage stays null, and the section renders nothing rather than
        // rendering a confident zero.
        if (error) return
        const passedBy = new Map()
        for (const r of data || []) {
          const n = r.steps?.number
          if (!n) continue
          if (r.status === 'check_passed' || r.status === 'signed_off') {
            if (!passedBy.has(n)) passedBy.set(n, [])
            passedBy.get(n).push(r.user_id)
          }
        }
        setCoverage({ passedBy, total: ids.length })
      })
  }, [members])

  const nameOf = (id) => {
    const m = (members || []).find((x) => x.id === id)
    return m ? firstNameFor(m, m) : 'a teammate'
  }

  let thinnest = null
  let helpers = []
  if (coverage && coverage.total > 0) {
    for (let n = 1; n <= 13; n += 1) {
      const c = (coverage.passedBy.get(n) || []).length
      if (c < coverage.total && (thinnest === null || c < thinnest.count)) thinnest = { step: n, count: c }
    }
    // Somebody who has cleared a step that the floor has not is worth asking.
    for (let n = 13; n >= 1; n -= 1) {
      const who = coverage.passedBy.get(n) || []
      if (who.length > 0 && who.length < coverage.total && helpers.length < 3) {
        helpers.push({ step: n, id: who[0] })
      }
    }
  }

  if (!dealershipId) return null

  return (
    <VStack align="stretch" spacing={7} mb={9}>
      {awaiting.length > 0 && (
        <Box>
          <Text fontWeight={600} mb={1} letterSpacing="-0.01em">
            Waiting on you
          </Text>
          <Text fontSize="body" color="inkMuted" mb={3} lineHeight={1.6}>
            Somebody logged a step that took both of you. One tap says it happened.
          </Text>
          <VStack align="stretch" spacing={2}>
            {awaiting.map((h) => (
              <HStack key={h.id} justify="space-between" py={2} borderTop="1px solid" borderColor="line">
                <Box>
                  <Text fontSize="body">
                    {nameOf(h.salesperson_id)} • {HANDOFF_KINDS[h.step_number]?.label || `Step ${h.step_number}`}
                  </Text>
                  <Text fontSize="sm" color="inkDim">
                    {h.briefed ? 'Briefed you first' : 'No brief logged'}
                  </Text>
                </Box>
                <Button size="sm" leftIcon={<Check size={15} />} onClick={() => confirm(h.id)} isLoading={busy === h.id}>
                  Confirm
                </Button>
              </HStack>
            ))}
          </VStack>
        </Box>
      )}

      {thinnest && (
        <Box>
          <Text fontWeight={600} mb={1} letterSpacing="-0.01em">
            Thinnest step right now
          </Text>
          <Text fontSize="body" color="inkMuted" lineHeight={1.6}>
            Step {thinnest.step}, {stepTitle(thinnest.step)}. {thinnest.count} of {coverage.total} have passed it.
            That is a gap in the process rather than in anybody, and it is the one worth a floor meeting.
          </Text>
        </Box>
      )}

      {helpers.length > 0 && (
        <Box>
          <Text fontWeight={600} mb={1} letterSpacing="-0.01em">
            Who to ask
          </Text>
          <Text fontSize="body" color="inkMuted" mb={2} lineHeight={1.6}>
            These people have already cleared a step somebody else has not. Ninety seconds from them beats
            a paragraph from anyone.
          </Text>
          <VStack align="stretch" spacing={1}>
            {helpers.map((h) => (
              <Text key={h.step} fontSize="body">
                Step {h.step}, {stepTitle(h.step)} • ask {nameOf(h.id)}
              </Text>
            ))}
          </VStack>
        </Box>
      )}
    </VStack>
  )
}
